import React, { useEffect, useState } from 'react';
import {
  Alert,
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, ThemeProvider } from '@mui/material/styles';
import {
  AccessTime as AccessTimeIcon,
  Build as BuildIcon,
  CalendarMonth,
  Dashboard as DashboardIcon,
  DirectionsRun as ExerciseIcon,
  EventNote as EventNoteIcon,
  FitnessCenter,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  MoreHoriz as MoreHorizIcon,
  Notifications as NotificationsIcon,
  PlayArrow as PlayIcon,
  Settings,
  SmartToy as AiAssistantIcon,
  Store as StoreIcon,
  Straighten as StraightenIcon,
  Timeline,
  AdminPanelSettings as AdminIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import TrainingPrograms from './components/programs/TrainingPrograms';
import Dashboard from './components/Dashboard';
import WorkoutLogger from './components/workouts/WorkoutLogger';
import Progress from './components/Progress';
import ExerciseManager from './components/exercises/ExerciseManager';
import BodyMeasurements from './components/BodyMeasurements';
import EquipmentManager from './components/EquipmentManager';
import GymManager from './components/GymManager';
import WorkoutPlanner from './components/workouts/WorkoutPlanner';
import Calendar from './components/Calendar';
import AiAssistant from './components/AiAssistant';
import CoachNotifications from './components/CoachNotifications';
import ProgressEstimation from './components/ProgressEstimation';
import OtherSports from './components/OtherSports';
import AdminAgent from './components/AdminAgent';
import NutritionPage from './components/NutritionPage';
import SettingsPage from './components/Settings';
import Login from './components/Login';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { WorkoutPlanService } from './services/workoutPlanService';
import { CoachNotificationService } from './services/coachNotificationService';
import type { CoachNotification, NextTrainerRunInfo } from './services/types';
import { API_BASE_URL, PROGRESS_ESTIMATION_ENABLED } from './config';
import { getImageUrl } from './services/api';
import { appTheme } from './theme';

const GoogleOAuthContext = React.createContext<boolean>(false);
export const useGoogleOAuth = () => React.useContext(GoogleOAuthContext);

const DRAWER_WIDTH = 286;

const baseMenuItems = [
  { text: 'Начало', icon: <DashboardIcon />, path: '/' },
  { text: 'Планирай', icon: <EventNoteIcon />, path: '/planner' },
  { text: 'Тренировка', icon: <PlayIcon />, path: '/workout' },
  { text: 'Програми', icon: <FitnessCenter />, path: '/programs' },
  { text: 'Упражнения', icon: <ExerciseIcon />, path: '/exercises' },
  { text: 'Зали', icon: <StoreIcon />, path: '/gyms' },
  { text: 'Оборудване', icon: <BuildIcon />, path: '/equipment' },
  { text: 'Календар', icon: <CalendarMonth />, path: '/calendar' },
  { text: 'Прогрес', icon: <Timeline />, path: '/progress' },
  { text: 'Измервания', icon: <StraightenIcon />, path: '/measurements' },
  { text: 'Хранене', icon: <RestaurantMenuIcon />, path: '/nutrition' },
  { text: 'AI Асистент', icon: <AiAssistantIcon />, path: '/ai' },
  { text: 'Нотификации', icon: <NotificationsIcon />, path: '/notifications' },
  { text: 'Admin Agent', icon: <AdminIcon />, path: '/admin' },
  { text: 'Настройки', icon: <Settings />, path: '/settings' },
];

const pageDescriptions: Record<string, string> = {
  '/': 'Планирай, започни и прегледай тренировките си от едно място.',
  '/planner': 'Подреди следващите сесии без да губиш контекст.',
  '/workout': 'Логвай тренировката с фокус върху изпълнението.',
  '/programs': 'Преглед и управление на активните програми.',
  '/exercises': 'Каталог и настройки за упражненията.',
  '/gyms': 'Работни зали и достъпното оборудване.',
  '/equipment': 'Поддържай списък с наличното оборудване.',
  '/calendar': 'Преглед на планирани и завършени сесии.',
  '/progress': 'История и метрики за развитието ти.',
  '/progress-estimation': 'Оценка на прогреса на база наличните данни.',
  '/measurements': 'Телесни измервания и промени във времето.',
  '/nutrition': 'Дневник на храненето и калориен баланс.',
  '/ai': 'Помощ от AI треньора за анализ и идеи.',
  '/notifications': 'Съвети и известия от треньора.',
  '/admin': 'Admin agent for system operations via SDK.',
  '/settings': 'Настройки на приложението.',
};

function PlannerRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const programId = searchParams.get('programId');

  return (
    <WorkoutPlanner
      onDone={() => navigate('/')}
      initialProgramId={programId ? Number(programId) : undefined}
    />
  );
}

function AppContent() {
  const TRAINER_BURST_POLL_MS = 5000;
  const TRAINER_BURST_WINDOW_MS = 2 * 60 * 1000;
  const coachAvatarUrl = getImageUrl('/img/task_01kjn79xvaebx94rsshgtetxj9_1772386336_img_1.webp');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [workoutProgramId, setWorkoutProgramId] = useState<number | null>(null);
  const [workoutDay, setWorkoutDay] = useState<number | null>(null);
  const [needsPlanning, setNeedsPlanning] = useState(false);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [coachNotifications, setCoachNotifications] = useState<CoachNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [trainerRunInfo, setTrainerRunInfo] = useState<NextTrainerRunInfo | null>(null);
  const [trainerRunLoading, setTrainerRunLoading] = useState(false);
  const [trainerRunError, setTrainerRunError] = useState<string | null>(null);
  const [trainerTimerAnchorEl, setTrainerTimerAnchorEl] = useState<null | HTMLElement>(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [burstPollingUntil, setBurstPollingUntil] = useState<number | null>(null);
  const [burstPollingBaselineUnread, setBurstPollingBaselineUnread] = useState(0);
  const [progressEstimationEnabled, setProgressEstimationEnabled] = useState(PROGRESS_ESTIMATION_ENABLED);
  const previousTrainerStateRef = React.useRef<NextTrainerRunInfo['state'] | null>(null);
  const { user, logout, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktopNav = useMediaQuery(appTheme.breakpoints.up('md'));
  const showInlineActions = useMediaQuery(appTheme.breakpoints.up('lg'));

  useEffect(() => {
    setMobileOpen(false);
    setActionsAnchorEl(null);
  }, [location.pathname]);

  useEffect(() => {
    if (isAuthenticated) {
      WorkoutPlanService.getPendingStatus()
        .then((status) => setNeedsPlanning(status.needsPlanning))
        .catch(() => {});
    }
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let isCancelled = false;

    const loadFeatureFlags = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/feature-flags`, {
          credentials: 'include',
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json() as { progressEstimation?: boolean };
        if (!isCancelled && typeof data.progressEstimation === 'boolean') {
          setProgressEstimationEnabled(PROGRESS_ESTIMATION_ENABLED && data.progressEstimation);
        }
      } catch {
        // Keep build-time flag fallback when runtime endpoint is unavailable.
      }
    };

    void loadFeatureFlags();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const refreshNextTrainerRun = async () => {
      try {
        setTrainerRunError(null);
        const nextRun = await CoachNotificationService.getNextTrainerRun();
        setTrainerRunInfo(nextRun);
      } catch {
        setTrainerRunError('Неуспешно зареждане на таймера.');
      }
    };

    void refreshNextTrainerRun();
    const interval = window.setInterval(refreshNextTrainerRun, 30000);
    const eventHandler = () => {
      void refreshNextTrainerRun();
    };
    window.addEventListener('trainer-schedule-updated', eventHandler);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('trainer-schedule-updated', eventHandler);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const currentState = trainerRunInfo?.state ?? null;
    const previousState = previousTrainerStateRef.current;

    if (currentState === 'due' && previousState !== 'due') {
      setBurstPollingBaselineUnread(unreadCount);
      setBurstPollingUntil(Date.now() + TRAINER_BURST_WINDOW_MS);
    } else if (currentState !== 'due' && previousState === 'due') {
      setBurstPollingUntil(null);
    }

    previousTrainerStateRef.current = currentState;
  }, [TRAINER_BURST_WINDOW_MS, trainerRunInfo?.state, unreadCount]);

  useEffect(() => {
    const interval = window.setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  const handleStartWorkout = (programId: number, dayOfWeek: number) => {
    setWorkoutProgramId(programId);
    setWorkoutDay(dayOfWeek);
    navigate('/workout');
  };

  const handlePlanWorkout = () => {
    navigate('/planner');
    setActionsAnchorEl(null);
  };

  const handleOpenNotificationsPage = () => {
    setNotificationAnchorEl(null);
    setActionsAnchorEl(null);
    navigate('/notifications');
  };

  const handleViewPrograms = () => {
    navigate('/programs');
  };

  const loadNotificationPreview = async () => {
    const response = await CoachNotificationService.getNotifications();
    setCoachNotifications(response.notifications.slice(0, 8));

    const unread = response.notifications.filter((notification) => !notification.isRead).length;
    setUnreadCount(unread);

    return unread;
  };

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let isCancelled = false;

    const refreshNotifications = async () => {
      const now = Date.now();
      const isBurstActive = burstPollingUntil !== null && burstPollingUntil > now;

      try {
        if (isBurstActive || Boolean(notificationAnchorEl)) {
          const unread = await loadNotificationPreview();
          if (isCancelled) {
            return;
          }

          if (isBurstActive && unread > burstPollingBaselineUnread) {
            setBurstPollingUntil(null);
          }
        } else {
          const count = await CoachNotificationService.getUnreadCount();
          if (!isCancelled) {
            setUnreadCount(count);
          }
        }
      } catch {
        if (!isCancelled && isBurstActive && burstPollingUntil !== null && Date.now() >= burstPollingUntil) {
          setBurstPollingUntil(null);
        }
      }

      if (!isCancelled && burstPollingUntil !== null && Date.now() >= burstPollingUntil) {
        setBurstPollingUntil(null);
      }
    };

    void refreshNotifications();

    const interval = window.setInterval(
      refreshNotifications,
      burstPollingUntil !== null ? TRAINER_BURST_POLL_MS : 30000,
    );

    return () => {
      isCancelled = true;
      window.clearInterval(interval);
    };
  }, [TRAINER_BURST_POLL_MS, burstPollingBaselineUnread, burstPollingUntil, isAuthenticated, notificationAnchorEl]);

  const handleOpenNotificationsMenu = async (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
    try {
      await loadNotificationPreview();
    } catch {
      setCoachNotifications([]);
    }
  };

  const handleMarkNotificationRead = async (notificationId: number) => {
    try {
      const response = await CoachNotificationService.markAsRead(notificationId);
      setUnreadCount(response.unreadCount);
      setCoachNotifications((prev) => prev.map((item) => (
        item.id === notificationId ? { ...item, isRead: true } : item
      )));
    } catch {
      // Ignore this interaction failure.
    }
  };

  const loadNextTrainerRun = async () => {
    try {
      setTrainerRunLoading(true);
      setTrainerRunError(null);
      const data = await CoachNotificationService.getNextTrainerRun();
      setTrainerRunInfo(data);
    } catch {
      setTrainerRunError('Неуспешно зареждане на таймера.');
    } finally {
      setTrainerRunLoading(false);
    }
  };

  const handleOpenTrainerTimer = async (event: React.MouseEvent<HTMLElement>) => {
    setTrainerTimerAnchorEl(event.currentTarget);
    await loadNextTrainerRun();
  };

  const formatRemaining = (remainingSeconds: number): string => {
    const total = Math.max(0, remainingSeconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    if (hours > 0) {
      return `${hours}ч ${minutes.toString().padStart(2, '0')}м ${seconds.toString().padStart(2, '0')}с`;
    }

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  const computedRemainingSeconds = trainerRunInfo?.scheduled && trainerRunInfo.nextRunAt
    ? Math.max(0, Math.floor((new Date(trainerRunInfo.nextRunAt).getTime() - currentTime) / 1000))
    : null;
  const trainerState = trainerRunInfo?.state ?? 'idle';
  const burstPollingActive = burstPollingUntil !== null && burstPollingUntil > Date.now();
  const trainerFailureMessage = trainerRunInfo?.lastExecution?.errorMessage || 'Възникна проблем при обработката на съветите. Системата ще опита отново по-късно.';
  const menuItems = [
    ...baseMenuItems,
    ...(progressEstimationEnabled ? [{ text: 'Progress Estimation', icon: <Timeline />, path: '/progress-estimation' }] : []),
  ];
  const currentPath = location.pathname;
  const currentTitle = menuItems.find((item) => item.path === currentPath)?.text || 'Gym Assistant';
  const currentDescription = pageDescriptions[currentPath] || 'Подреди важните действия за деня без излишен шум.';
  const displayName = user?.firstName || user?.username || 'Профил';

  const navigationContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        gap: 2,
      }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          background: `linear-gradient(160deg, ${alpha('#7dd3fc', 0.18)} 0%, ${alpha('#8bffb0', 0.1)} 100%)`,
        }}
      >
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha('#7dd3fc', 0.18), color: 'primary.main' }}>
              <FitnessCenter />
            </Avatar>
            <Box>
              <Typography variant="subtitle2">Gym Assistant</Typography>
              <Typography variant="caption" color="text.secondary">
                Mobile-first workout command center
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', display: 'block', px: 1.5, mb: 1 }}
        >
          Навигация
        </Typography>
        <List disablePadding sx={{ display: 'grid', gap: 0.75 }}>
          {menuItems.map((item) => {
            const selected = item.path === currentPath || (item.path === '/' && currentPath === '');

            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={selected}
                  aria-current={selected ? 'page' : undefined}
                  onClick={() => navigate(item.path)}
                  sx={{
                    px: 1.5,
                    py: 1,
                    alignItems: 'center',
                    '&:hover': {
                      backgroundColor: alpha('#7dd3fc', 0.08),
                    },
                    '& .MuiListItemIcon-root': {
                      minWidth: 40,
                      color: selected ? 'primary.main' : 'text.secondary',
                    },
                    '& .MuiListItemText-primary': {
                      fontWeight: selected ? 700 : 600,
                    },
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Card elevation={0} sx={{ borderRadius: 4, backgroundColor: alpha('#172033', 0.76) }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle2" gutterBottom>
            Днес
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {needsPlanning ? 'Имаш чакащо планиране за следващата тренировка.' : 'Няма спешни задачи. Можеш да стартираш или прегледаш последните си тренировки.'}
          </Typography>
          {needsPlanning && (
            <Button
              variant="contained"
              size="small"
              startIcon={<WarningIcon />}
              onClick={handlePlanWorkout}
              sx={{ mt: 2 }}
            >
              Планирай
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }, ml: { md: `${DRAWER_WIDTH}px` } }}>
        <Toolbar sx={{ minHeight: { xs: 72, md: 88 }, px: { xs: 1.5, sm: 2.5 }, gap: 1.25 }}>
          {!isDesktopNav && (
            <IconButton
              color="inherit"
              aria-label="Отвори менюто"
              data-testid="mobile-nav-trigger"
              onClick={() => setMobileOpen(true)}
              edge="start"
            >
              <MenuIcon />
            </IconButton>
          )}

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                display: { xs: 'none', sm: 'flex' },
                bgcolor: alpha('#7dd3fc', 0.14),
                color: 'primary.main',
              }}
            >
              <FitnessCenter fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Gym Assistant
              </Typography>
              <Typography variant="h6" noWrap>
                {currentTitle}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 520 }}
              >
                {currentDescription}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
            <Tooltip title="Нотификации">
              <IconButton color="inherit" aria-label="Отвори нотификациите" onClick={handleOpenNotificationsMenu}>
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Таймер на треньора">
              <IconButton color="inherit" aria-label="Отвори таймера на треньора" onClick={handleOpenTrainerTimer}>
                <Badge
                  color={trainerState === 'failed' ? 'error' : 'warning'}
                  variant={trainerState === 'scheduled' || trainerState === 'due' ? 'dot' : 'standard'}
                  badgeContent={trainerState === 'failed' ? '!' : undefined}
                  overlap="circular"
                >
                  <Avatar
                    src={coachAvatarUrl}
                    alt="Coach Nova"
                    sx={{ width: 30, height: 30, border: `1px solid ${alpha('#e2e8f0', 0.24)}` }}
                  />
                </Badge>
              </IconButton>
            </Tooltip>

            {showInlineActions ? (
              <>
                {needsPlanning && (
                  <Button
                    color="warning"
                    variant="outlined"
                    startIcon={<WarningIcon />}
                    onClick={handlePlanWorkout}
                    sx={{ borderColor: alpha('#fbbf24', 0.4), color: 'warning.main' }}
                  >
                    Планирай
                  </Button>
                )}
                <Chip
                  label={displayName}
                  variant="outlined"
                  sx={{ maxWidth: 180, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                />
                <Button color="inherit" onClick={logout} startIcon={<LogoutIcon />} aria-label="Изход">
                  Изход
                </Button>
              </>
            ) : (
              <IconButton
                color="inherit"
                aria-label="Още действия"
                data-testid="header-actions-trigger"
                onClick={(event) => setActionsAnchorEl(event.currentTarget)}
              >
                <MoreHorizIcon />
              </IconButton>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Box component="nav" aria-label="navigation" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 'min(88vw, 320px)',
            },
          }}
        >
          <Box data-testid="mobile-navigation-drawer" sx={{ height: '100%' }}>
            {navigationContent}
          </Box>
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {navigationContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'clip',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 72, md: 88 } }} />
        <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 2.5, md: 4 }, pb: 4, pt: { xs: 2, md: 3 } }}>
          <Routes>
            <Route
              path="/"
              element={(
                <Dashboard
                  onStartWorkout={handleStartWorkout}
                  onViewPrograms={handleViewPrograms}
                  onPlanWorkout={handlePlanWorkout}
                  onOpenNotifications={handleOpenNotificationsPage}
                />
              )}
            />
            <Route path="/planner" element={<PlannerRoute />} />
            <Route
              path="/workout"
              element={(
                <WorkoutLogger
                  initialProgramId={workoutProgramId || undefined}
                  initialDay={workoutDay || undefined}
                  onNavigateToPlanner={handlePlanWorkout}
                />
              )}
            />
            <Route path="/programs" element={<TrainingPrograms />} />
            <Route path="/exercises" element={<ExerciseManager />} />
            <Route path="/gyms" element={<GymManager />} />
            <Route path="/equipment" element={<EquipmentManager />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/progress" element={<Progress />} />
            <Route path="/progress-estimation" element={<ProgressEstimation />} />
            <Route path="/measurements" element={<BodyMeasurements />} />
            <Route path="/nutrition" element={<NutritionPage />} />
            <Route path="/ai" element={<AiAssistant />} />
            <Route path="/notifications" element={<CoachNotifications />} />
            <Route path="/admin" element={<AdminAgent />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Container>
        <Box
          component="footer"
          sx={{
            mt: 'auto',
            px: { xs: 2, md: 4 },
            pb: 3,
          }}
        >
          <Card elevation={0} sx={{ borderRadius: 4, backgroundColor: alpha('#0f172a', 0.72) }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                © 2024 Gym Assistant. Подреди деня си, преди да започнеш следващата тренировка.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={() => setActionsAnchorEl(null)}
        PaperProps={{ sx: { width: 260, mt: 1.5 } }}
      >
        <MenuItem disabled sx={{ opacity: 1 }}>
          <Stack spacing={0.25}>
            <Typography variant="subtitle2">{displayName}</Typography>
            <Typography variant="caption" color="text.secondary">
              Secondary actions
            </Typography>
          </Stack>
        </MenuItem>
        {needsPlanning && (
          <MenuItem onClick={handlePlanWorkout}>
            <ListItemIcon>
              <WarningIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText primary="Планирай" />
          </MenuItem>
        )}
        <MenuItem onClick={handleOpenNotificationsPage}>
          <ListItemIcon>
            <NotificationsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Всички нотификации" />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setActionsAnchorEl(null);
            logout();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Изход" />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={() => setNotificationAnchorEl(null)}
        PaperProps={{ sx: { width: 360, maxWidth: 'calc(100vw - 24px)', mt: 1.5 } }}
      >
        <Card elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider', borderRadius: 0 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                <AiAssistantIcon fontSize="small" />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">Coach Nova</Typography>
                <Typography variant="caption" color="text.secondary">
                  {unreadCount > 0 ? `${unreadCount} нови съвета` : 'Няма нови съвети'}
                </Typography>
              </Box>
              <Button size="small" onClick={handleOpenNotificationsPage}>
                Виж всички
              </Button>
            </Stack>
          </CardContent>
        </Card>
        {coachNotifications.length === 0 ? (
          <MenuItem disabled>Няма нотификации</MenuItem>
        ) : (
          coachNotifications.map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => {
                if (!notification.isRead) {
                  void handleMarkNotificationRead(notification.id);
                }
              }}
              sx={{
                py: 1.25,
                alignItems: 'flex-start',
                backgroundColor: notification.isRead ? 'transparent' : alpha('#7dd3fc', 0.08),
                whiteSpace: 'normal',
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  {notification.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {notification.message}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
        <Divider />
        <MenuItem onClick={handleOpenNotificationsPage}>Отвори екрана с нотификации</MenuItem>
      </Menu>

      <Popover
        anchorEl={trainerTimerAnchorEl}
        open={Boolean(trainerTimerAnchorEl)}
        onClose={() => setTrainerTimerAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 320, maxWidth: 'calc(100vw - 24px)', p: 2, mt: 1.5 } }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <Avatar src={coachAvatarUrl} alt="Coach Nova" sx={{ width: 34, height: 34 }} />
          <Box>
            <Typography variant="subtitle2">Coach Nova</Typography>
            <Typography variant="caption" color="text.secondary">
              Статус на треньора
            </Typography>
          </Box>
        </Stack>
        {trainerRunLoading ? (
          <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={20} />
          </Box>
        ) : trainerRunError ? (
          <Alert severity="error">{trainerRunError}</Alert>
        ) : trainerState === 'scheduled' && computedRemainingSeconds !== null ? (
          <Box>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
              Следващо пускане
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <AccessTimeIcon fontSize="small" color="warning" />
              <Typography variant="body2" fontWeight={700}>
                {formatRemaining(computedRemainingSeconds)}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {new Date(trainerRunInfo.nextRunAt as string).toLocaleString('bg-BG')}
            </Typography>
          </Box>
        ) : trainerState === 'due' ? (
          <Alert severity="info">
            Треньорът обработва съветите…{burstPollingActive ? ' Обновяваме нотификациите автоматично.' : ''}
          </Alert>
        ) : trainerState === 'failed' ? (
          <Box>
            <Alert severity="warning" sx={{ mb: 1 }}>
              {trainerFailureMessage}
            </Alert>
            <Typography variant="caption" color="text.secondary">
              Ще бъде направен нов опит при следваща тренировка или след ново насрочване.
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Няма насрочено пускане.
          </Typography>
        )}
      </Popover>
    </Box>
  );
}

function App() {
  const [googleClientId, setGoogleClientId] = useState(process.env.REACT_APP_GOOGLE_CLIENT_ID || '');

  useEffect(() => {
    if (googleClientId) {
      return;
    }

    let isCancelled = false;

    const loadGoogleClientId = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/google/client-id`, {
          credentials: 'include',
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        if (!isCancelled && typeof data.clientId === 'string' && data.clientId) {
          setGoogleClientId(data.clientId);
        }
      } catch {
        // Google login remains hidden if config endpoint is unavailable.
      }
    };

    void loadGoogleClientId();

    return () => {
      isCancelled = true;
    };
  }, [googleClientId]);

  const appContent = (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <GoogleOAuthContext.Provider value={!!googleClientId}>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </GoogleOAuthContext.Provider>
    </ThemeProvider>
  );

  if (googleClientId) {
    return (
      <GoogleOAuthProvider clientId={googleClientId}>
        {appContent}
      </GoogleOAuthProvider>
    );
  }

  return appContent;
}

export default App;
